import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import authService from '../../services/authService'
import actions from './actions'
import { authState, usersReportAccess } from './userTypes'
import jwtDecode from 'jwt-decode'
import { IAuthJWT } from '../../config/axios'
import { RootState } from '..'
import { APP_MODE } from '../../sharedTypes'

import { error } from '../../utils/alertUtils';

const initialState: authState = {
  access: '',
  refresh: '',
  fulfilled: false,
  rejected: false,
  fetching: false,
  errorMessage: '',
  userRoles: [],
  userType: '',
  userProfileId: 0,
  organizationId: undefined,
  organizationType: APP_MODE.full,
  organizationTitle: '',
  userDetails: {
    role: undefined,
    userID: undefined,
    user: {
      first_name: '',
      last_name: '',
    },
  },
  userEditAccessOnReport: false,
}

export const loginUser = createAsyncThunk(
  actions.AUTH_LOGIN,
  async (payload: { email: string; password: string }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await authService.loginUser(payload.email, payload.password)
      const tokenData: IAuthJWT = jwtDecode(data?.access)
      if (tokenData?.type === 'SYS_ADMIN') {
        error('System Admin does not have access to Investigation App')
        return rejectWithValue({})
      }
      localStorage.setItem('accessToken', data.access)
      localStorage.setItem('refreshToken', data.refresh)
      dispatch(getUserDetails({ userId: tokenData.user_id }))
      return { data, tokenData }
    } catch (err) {
      if (err instanceof Error) {
        error('Login failed. Please try again.')
        return rejectWithValue('Login failed')
      }
    }
  },
)

export const forgotPassword = createAsyncThunk(
  actions.FORGOT_PASSWORD,
  async (payload: { email: string }, { rejectWithValue }) => {
    try {
      const { data } = await authService.forgotPassword(payload.email)
      return data
    } catch (err) {
      if (err instanceof Error) {
        error('Reset password failed. Please try again')
        return rejectWithValue('Forgot Password failed')
      }
    }
  },
)

export const resetPassword = createAsyncThunk(
  actions.RESET_PASSWORD,
  async (payload: { password: string; token: string }, { rejectWithValue }) => {
    try {
      const { data } = await authService.resetPassword(payload.password, payload.token)
      return data
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue('Reset Password failed')
      }
    }
  },
)

export const getUserDetails = createAsyncThunk(
  actions.GET_USER_DETAILS,
  async (payload: { userId: string }, { rejectWithValue }) => {
    try {
      const { data } = await authService.getUserDetails(payload.userId)
      const formatedRoles = data?.roles.map((role: { text: any }) => role?.text)
      return { ...data, roles: formatedRoles }
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue('Reset Password failed')
      }
    }
  },
)

export const getUserAccessOnReport = createAsyncThunk(
  actions.GET_USER_REPORT_ACCESS,
  async (payload: { reportId: number; caseStatus?: string }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState
      const { data } = await authService.getUserReportAccess(
        payload?.reportId,
        state?.auth.userDetails?.userID as number,
      )
      
      if (data[0]?.access === usersReportAccess.CAN_EDIT && payload.caseStatus !== 'ARCHIVED') {
        return true
      }
      return false
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue('User Report Access failed')
      }
    }
  },
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logOut: state => {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      state = initialState
      return state
    },
  },
  extraReducers: builder => {
    builder.addCase(loginUser.fulfilled, (state, { payload }) => {
      state.fetching = false
      state.fulfilled = true
      if (payload?.data.access) state.access = payload?.data.access
      if (payload?.data.refresh) state.refresh = payload?.data.refresh
      if (payload?.tokenData?.type) state.userType = payload?.tokenData?.type
      if (payload?.tokenData?.roles) state.userRoles = payload?.tokenData?.roles
    }),
      builder.addCase(loginUser.rejected, state => {
        state.fetching = false
        state.rejected = true
      })
    builder.addCase(loginUser.pending, state => {
      state.fetching = true
    }),
      builder.addCase(getUserDetails.fulfilled, (state, { payload }) => {

        state.organizationId = payload?.organization
        state.organizationType = payload?.organizationObj?.organization_type || APP_MODE.full
        state.organizationTitle = payload?.organizationTitle
        state.userRoles = payload?.roles
        state.userDetails.userID = payload?.user?.id
        state.userProfileId = payload?.id
        state.userDetails.user = payload?.user

      })
    builder.addCase(getUserAccessOnReport.fulfilled, (state, { payload }) => {
      // console.log('userEditAccessOnReport', payload)
      state.userEditAccessOnReport = payload as unknown as boolean
    })
  },
})

export const { logOut } = authSlice.actions

export default authSlice.reducer
